
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { LogicNode } from './types';

interface NodePropertiesPanelProps {
  node: LogicNode;
  onClose: () => void;
  onUpdate: (nodeId: string, data: any) => void;
}

export default function NodePropertiesPanel({ node, onClose, onUpdate }: NodePropertiesPanelProps) {
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(node.id, { label: e.target.value });
  };

  const handleConditionTypeChange = (value: string) => {
    onUpdate(node.id, { 
      condition: { 
        ...node.data.condition, 
        type: value 
      } 
    });
  };

  const handleConditionParamChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(node.id, { 
      condition: { 
        ...node.data.condition, 
        parameter: e.target.value 
      } 
    });
  };

  const handleActionTypeChange = (value: string) => {
    onUpdate(node.id, { 
      action: { 
        ...node.data.action, 
        type: value 
      } 
    });
  };

  const handleActionSystemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(node.id, { 
      action: { 
        ...node.data.action, 
        system: e.target.value 
      } 
    });
  };

  const handleActionDocChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(node.id, { 
      action: { 
        ...node.data.action, 
        documentation: e.target.value 
      } 
    });
  };

  const handleActionNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(node.id, { 
      action: { 
        ...node.data.action, 
        notes: e.target.value 
      } 
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Node Properties</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X size={18} />
        </Button>
      </div>
      
      <div className="space-y-2">
        <div className="grid gap-2">
          <label className="text-sm font-medium">Label</label>
          <Input 
            value={node.data?.label || ''} 
            onChange={handleLabelChange}
            placeholder="Node label"
          />
        </div>

        {node.data?.type === 'condition' && (
          <>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Condition Type</label>
              <Select 
                value={node.data?.condition?.type || 'manual'} 
                onValueChange={handleConditionTypeChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select condition type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vin_feature">VIN Feature</SelectItem>
                  <SelectItem value="repair_item">Repair Item</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Condition Parameter</label>
              <Input 
                value={node.data?.condition?.parameter || ''} 
                onChange={handleConditionParamChange}
                placeholder="E.g., Has radar sensor? or Bumper replaced?"
              />
            </div>
          </>
        )}

        {node.data?.type === 'action' && (
          <>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Action Type</label>
              <Select 
                value={node.data?.action?.type || 'calibration'} 
                onValueChange={handleActionTypeChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select action type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="calibration">Calibration</SelectItem>
                  <SelectItem value="repair_procedure">Repair Procedure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">System</label>
              <Input 
                value={node.data?.action?.system || ''} 
                onChange={handleActionSystemChange}
                placeholder="E.g., Front Radar, Camera, Lidar"
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Documentation</label>
              <Textarea 
                value={node.data?.action?.documentation || ''} 
                onChange={handleActionDocChange}
                placeholder="OEM documentation reference"
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea 
                value={node.data?.action?.notes || ''} 
                onChange={handleActionNotesChange}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
